import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Save, X, Shield, CheckSquare, AlertTriangle, Link2 } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { PAGE_COLORS } from '../../utils/pageColors';
import {
  useGetRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useGetPermissionsByCategoryQuery,
} from '../../store/slices/apiSlice';
import { roleSchema, type RoleFormData, getFormErrorMessage, getFieldErrors } from '../../utils/validation';
import type { PermissionSummary, PermissionsByCategory, RoleFormPageProps } from '../../types';
import { useToast } from '../../hooks/useToast';
import {
  ALL_CATALOG_PATHS,
  NAV_TOP_ITEMS,
  ROLE_PAGE_GROUPS,
} from '../../navigation/navCatalog';
import type { NavGroup } from '../../navigation/navCatalog';
import {
  PAGE_PATH_VIEW_ALTERNATIVES,
  getContractPermissionCodes,
  getPermissionCodeToPagePaths,
} from '../../navigation/permissionPageMap';

function uniqueCodesInOrder(codes: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of codes) {
    if (seen.has(c)) continue;
    seen.add(c);
    out.push(c);
  }
  return out;
}

export const RoleFormPage: React.FC<RoleFormPageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const toast = useToast();

  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [selectedPagePaths, setSelectedPagePaths] = useState<Set<string>>(
    () => new Set(ALL_CATALOG_PATHS)
  );

  const { data: roleData, isLoading: isLoadingRole } = useGetRoleQuery(id!, {
    skip: !isEditing,
  });
  const { data: permissionsData, isLoading: isLoadingPermissions } =
    useGetPermissionsByCategoryQuery();

  const pageSelectionGroups = useMemo(
    (): NavGroup[] => [{ label: 'Main', items: NAV_TOP_ITEMS }, ...ROLE_PAGE_GROUPS],
    []
  );
  const pageLabelByPath = useMemo(() => {
    const out = new Map<string, string>();
    pageSelectionGroups.forEach((g) => {
      g.items.forEach((i) => out.set(i.path, i.label));
    });
    return out;
  }, [pageSelectionGroups]);

  const contractPermissionCodes = useMemo(() => getContractPermissionCodes(), []);
  const codeToPagePaths = useMemo(() => getPermissionCodeToPagePaths(), []);

  const flatPermissionList = useMemo((): PermissionSummary[] => {
    if (!permissionsData?.categories?.length) return [];
    return permissionsData.categories.flatMap((c) => c.permissions);
  }, [permissionsData]);

  const permissionById = useMemo(() => {
    const m = new Map<string, PermissionSummary>();
    flatPermissionList.forEach((p) => m.set(p.id, p));
    return m;
  }, [flatPermissionList]);

  const permissionIdByCode = useMemo(() => {
    const m = new Map<string, string>();
    flatPermissionList.forEach((p) => {
      if (!m.has(p.code)) m.set(p.code, p.id);
    });
    return m;
  }, [flatPermissionList]);

  const permissionCodeById = useMemo(() => {
    const out = new Map<string, string>();
    flatPermissionList.forEach((p) => out.set(p.id, p.code));
    return out;
  }, [flatPermissionList]);

  const selectedPermissionCodes = useMemo(() => {
    const out = new Set<string>();
    selectedPermissions.forEach((permId) => {
      const code = permissionCodeById.get(permId);
      if (code) out.add(code);
    });
    return out;
  }, [selectedPermissions, permissionCodeById]);

  const pagePermissionGaps = useMemo(() => {
    const gaps: Array<{ path: string; requiredCodes: readonly string[] }> = [];
    selectedPagePaths.forEach((path) => {
      const requiredCodes = PAGE_PATH_VIEW_ALTERNATIVES[path];
      if (!requiredCodes || requiredCodes.length === 0) return;
      const hasAny = requiredCodes.some((code) => selectedPermissionCodes.has(code));
      if (!hasAny) {
        gaps.push({ path, requiredCodes });
      }
    });
    return gaps.sort((a, b) => a.path.localeCompare(b.path));
  }, [selectedPagePaths, selectedPermissionCodes]);

  const orphanedContractSelections = useMemo(() => {
    const out: PermissionSummary[] = [];
    selectedPermissions.forEach((permId) => {
      const code = permissionCodeById.get(permId);
      if (!code || !contractPermissionCodes.has(code)) return;
      const paths = codeToPagePaths.get(code);
      if (!paths?.length) return;
      if (!paths.some((p) => selectedPagePaths.has(p))) {
        const row = permissionById.get(permId);
        if (row) out.push(row);
      }
    });
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [
    selectedPermissions,
    permissionCodeById,
    contractPermissionCodes,
    codeToPagePaths,
    selectedPagePaths,
    permissionById,
  ]);

  const additionalPermissionCategories = useMemo(() => {
    if (!permissionsData?.categories?.length) return [];
    return permissionsData.categories
      .map((cat) => ({
        ...cat,
        permissions: cat.permissions.filter((p) => !contractPermissionCodes.has(p.code)),
      }))
      .filter((cat) => cat.permissions.length > 0);
  }, [permissionsData, contractPermissionCodes]);

  const [createRole, { isLoading: isCreating }] = useCreateRoleMutation();
  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
    reset,
  } = useForm<RoleFormData>({
    resolver: zodResolver(roleSchema),
    defaultValues: {
      name: '',
      description: '',
      permission_ids: [],
      visible_page_paths: [...ALL_CATALOG_PATHS],
    },
  });

  useEffect(() => {
    if (isEditing && roleData) {
      const paths =
        roleData.visible_page_paths?.length > 0
          ? roleData.visible_page_paths
          : ALL_CATALOG_PATHS;
      reset({
        name: roleData.name,
        description: roleData.description,
        permission_ids: roleData.permissions.map((p) => p.id),
        visible_page_paths: paths,
      });
      setSelectedPermissions(new Set(roleData.permissions.map((p) => p.id)));
      setSelectedPagePaths(new Set(paths));
      return;
    }
    if (!isEditing) {
      reset({
        name: '',
        description: '',
        permission_ids: [],
        visible_page_paths: [...ALL_CATALOG_PATHS],
      });
      setSelectedPermissions(new Set());
      setSelectedPagePaths(new Set(ALL_CATALOG_PATHS));
    }
  }, [isEditing, roleData, reset]);

  const handlePermissionToggle = (permissionId: string) => {
    const newSelected = new Set(selectedPermissions);
    if (newSelected.has(permissionId)) {
      newSelected.delete(permissionId);
    } else {
      newSelected.add(permissionId);
    }
    setSelectedPermissions(newSelected);
    setValue('permission_ids', Array.from(newSelected));
  };

  const handleSelectAllInCategory = (category: PermissionsByCategory) => {
    const categoryPermissionIds = category.permissions.map((p) => p.id);
    const allSelected = categoryPermissionIds.every((id) => selectedPermissions.has(id));

    const newSelected = new Set(selectedPermissions);
    if (allSelected) {
      categoryPermissionIds.forEach((permId) => newSelected.delete(permId));
    } else {
      categoryPermissionIds.forEach((permId) => newSelected.add(permId));
    }

    setSelectedPermissions(newSelected);
    setValue('permission_ids', Array.from(newSelected));
  };

  const handlePagePathToggle = (path: string) => {
    const next = new Set(selectedPagePaths);
    if (next.has(path)) {
      next.delete(path);
    } else {
      next.add(path);
    }
    setSelectedPagePaths(next);
    setValue('visible_page_paths', Array.from(next));
  };

  const handleSelectAllPagesInGroup = (group: NavGroup) => {
    const pathsInGroup = group.items.map((i) => i.path);
    const allSelected = pathsInGroup.every((p) => selectedPagePaths.has(p));
    const next = new Set(selectedPagePaths);
    if (allSelected) {
      pathsInGroup.forEach((p) => next.delete(p));
    } else {
      pathsInGroup.forEach((p) => next.add(p));
    }
    setSelectedPagePaths(next);
    setValue('visible_page_paths', Array.from(next));
  };

  const onSubmit = async (data: RoleFormData) => {
    try {
      if (pagePermissionGaps.length > 0) {
        const first = pagePermissionGaps[0];
        const label = pageLabelByPath.get(first.path) ?? first.path;
        const required = first.requiredCodes.join(', ');
        const message = `Selected pages require matching permissions. Example: ${label} (${first.path}) needs one of: ${required}`;
        setError('visible_page_paths', { message });
        toast.error('Page/permission mismatch', message);
        return;
      }

      data.permission_ids = Array.from(selectedPermissions);
      data.visible_page_paths = Array.from(selectedPagePaths);

      if (isEditing) {
        await updateRole({ id: id!, data }).unwrap();
        toast.success(
          'Role Updated',
          `Role "${data.name}" has been successfully updated.`
        );
      } else {
        await createRole(data).unwrap();
        toast.success(
          'Role Created',
          `Role "${data.name}" has been successfully created.`
        );
      }

      navigate('/roles');
    } catch (error: unknown) {
      const errorMessage = getFormErrorMessage(error, 'Failed to save role');
      const fieldErrors = getFieldErrors(error);

      toast.error(
        isEditing ? 'Failed to Update Role' : 'Failed to Create Role',
        errorMessage
      );

      Object.entries(fieldErrors).forEach(([field, message]) => {
        setError(field as keyof RoleFormData, { message });
      });

      if (!Object.keys(fieldErrors).length) {
        setError('root', { message: errorMessage });
      }
    }
  };

  const isLoading = isLoadingRole || isLoadingPermissions;
  const isSubmitting = isCreating || isUpdating;

  const renderPermissionRow = (
    perm: PermissionSummary,
    pagePath: string,
    options?: { showSharedHint?: boolean }
  ) => {
    const isSelected = selectedPermissions.has(perm.id);
    const pathsForCode = codeToPagePaths.get(perm.code);
    const sharedLabels =
      options?.showSharedHint && pathsForCode && pathsForCode.length > 1
        ? pathsForCode
            .filter((p) => p !== pagePath)
            .map((p) => pageLabelByPath.get(p) ?? p)
        : [];

    return (
      <label
        key={`${pagePath}-${perm.id}`}
        className={`flex items-start p-3 rounded-md border cursor-pointer transition-colors ${
          isSelected
            ? 'bg-blue-50 border-blue-200'
            : 'bg-white border-gray-200 hover:bg-gray-50'
        }`}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => handlePermissionToggle(perm.id)}
          className="mt-0.5 mr-3 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900">{perm.name}</div>
          <div className="text-xs text-gray-500 font-mono">{perm.code}</div>
          {sharedLabels.length > 0 && (
            <div className="mt-1 flex items-start gap-1 text-[11px] text-slate-600">
              <Link2 className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
              <span>Also applies to: {sharedLabels.join(', ')}</span>
            </div>
          )}
        </div>
      </label>
    );
  };

  return (
    <AppShell>
      <PageLayout
        title={isEditing ? 'Edit Role' : 'Create Role'}
        subtitle={isEditing ? `Editing: ${roleData?.name}` : 'Create a new system role'}
        bgColor={PAGE_COLORS.settings.bgColor}
        textColor={PAGE_COLORS.settings.textColor}
        borderColor={PAGE_COLORS.settings.borderColor}
        icon={<Shield className="w-6 h-6" />}
      >
        {isLoading ? (
          <ContentCard>
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          </ContentCard>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <ContentCard title="Basic Information">
              <div className="space-y-4">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    Role Name *
                  </label>
                  <input
                    id="name"
                    type="text"
                    placeholder="e.g., Production Manager"
                    className={`form-input ${
                      errors.name
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                    {...register('name')}
                  />
                  {errors.name && <p className="form-error">{errors.name.message}</p>}
                </div>

                <div className="form-group">
                  <label htmlFor="description" className="form-label">
                    Description *
                  </label>
                  <textarea
                    id="description"
                    rows={3}
                    placeholder="Describe what this role is responsible for..."
                    className={`form-input ${
                      errors.description
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                    {...register('description')}
                  />
                  {errors.description && (
                    <p className="form-error">{errors.description.message}</p>
                  )}
                </div>
              </div>
            </ContentCard>

            <ContentCard
              title="Pages & permissions"
              subtitle={`${selectedPagePaths.size} pages enabled · ${selectedPermissions.size} permissions selected · same order as the main sidebar`}
            >
              <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                Turn on a page, then pick one or more permissions listed under it. Permissions
                shared across pages stay in sync. Use <span className="font-semibold">Additional API / legacy</span>{' '}
                at the bottom for backend-only permissions not tied to sidebar pages.
              </div>

              {errors.permission_ids && (
                <div className="alert alert-error mb-4">{errors.permission_ids.message}</div>
              )}

              {pagePermissionGaps.length > 0 && (
                <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900">
                  <div className="flex items-center gap-2 font-medium">
                    <AlertTriangle className="h-4 w-4" />
                    Some enabled pages need at least one matching permission
                  </div>
                  <ul className="mt-2 space-y-2 text-sm">
                    {pagePermissionGaps.slice(0, 8).map((gap) => (
                      <li key={gap.path}>
                        <span className="font-semibold">
                          {pageLabelByPath.get(gap.path) ?? gap.path}
                        </span>{' '}
                        <span className="font-mono text-xs">({gap.path})</span>
                        <div className="text-xs">Pick one of: {gap.requiredCodes.join(', ')}</div>
                      </li>
                    ))}
                  </ul>
                  {pagePermissionGaps.length > 8 && (
                    <p className="mt-2 text-xs">
                      +{pagePermissionGaps.length - 8} more page(s) need permissions.
                    </p>
                  )}
                </div>
              )}

              {orphanedContractSelections.length > 0 && (
                <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-slate-800">
                  <div className="text-sm font-medium">Selected access with no matching page enabled</div>
                  <p className="mt-1 text-xs text-slate-600">
                    These permissions are tied to sidebar pages that are currently turned off. Turn
                    a page on above to manage them in context, or turn the permission off here.
                  </p>
                  <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {orphanedContractSelections.map((perm) => (
                      <label
                        key={`orphan-${perm.id}`}
                        className={`flex items-start rounded-md border p-3 ${
                          selectedPermissions.has(perm.id)
                            ? 'border-blue-200 bg-blue-50'
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedPermissions.has(perm.id)}
                          onChange={() => handlePermissionToggle(perm.id)}
                          className="mt-0.5 mr-3 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900">{perm.name}</div>
                          <div className="text-xs text-gray-500 font-mono">{perm.code}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {errors.visible_page_paths && (
                <div className="alert alert-error mb-4">{errors.visible_page_paths.message}</div>
              )}

              <div className="space-y-6">
                {pageSelectionGroups.map((group) => {
                  const pathsInGroup = group.items.map((i) => i.path);
                  const selectedInGroup = pathsInGroup.filter((p) => selectedPagePaths.has(p)).length;
                  const allInGroup =
                    selectedInGroup === pathsInGroup.length && pathsInGroup.length > 0;
                  const someInGroup =
                    selectedInGroup > 0 && selectedInGroup < pathsInGroup.length;

                  return (
                    <div key={group.label} className="rounded-lg border border-gray-200 p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center">
                          <button
                            type="button"
                            onClick={() => handleSelectAllPagesInGroup(group)}
                            className={`mr-3 flex h-4 w-4 items-center justify-center rounded border-2 ${
                              allInGroup
                                ? 'border-blue-600 bg-blue-600'
                                : someInGroup
                                  ? 'border-blue-600 bg-blue-600'
                                  : 'border-gray-300 hover:border-gray-400'
                            }`}
                          >
                            {allInGroup && <CheckSquare className="h-3 w-3 text-white" />}
                            {someInGroup && !allInGroup && (
                              <div className="h-2 w-2 rounded-sm bg-white" />
                            )}
                          </button>
                          <h4 className="text-lg font-medium text-gray-900">{group.label}</h4>
                        </div>
                        <span className="text-sm text-gray-500">
                          {selectedInGroup}/{pathsInGroup.length} pages enabled
                        </span>
                      </div>

                      <div className="space-y-4">
                        {group.items.map((item) => {
                          const pageOn = selectedPagePaths.has(item.path);
                          const rawCodes = PAGE_PATH_VIEW_ALTERNATIVES[item.path] ?? [];
                          const orderedCodes = uniqueCodesInOrder(rawCodes);
                          const pageGap =
                            pageOn &&
                            orderedCodes.length > 0 &&
                            !orderedCodes.some((code) => selectedPermissionCodes.has(code));

                          return (
                            <div
                              key={item.path}
                              className={`rounded-md border p-3 ${
                                pageOn ? 'border-gray-200 bg-white' : 'border-dashed border-gray-200 bg-gray-50/60'
                              }`}
                            >
                              <label className="flex cursor-pointer items-start gap-3">
                                <input
                                  type="checkbox"
                                  checked={pageOn}
                                  onChange={() => handlePagePathToggle(item.path)}
                                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-semibold text-gray-900">{item.label}</div>
                                  <div className="font-mono text-xs text-gray-500">{item.path}</div>
                                  {pageGap && (
                                    <div className="mt-2 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                      <span>
                                        Select at least one permission below for this page (or turn
                                        the page off).
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </label>

                              {pageOn && (
                                <div className="mt-3 border-t border-gray-100 pt-3">
                                  {orderedCodes.length === 0 ? (
                                    <p className="text-xs text-gray-500">No contract mapping for this path.</p>
                                  ) : (
                                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                                      {orderedCodes.map((code) => {
                                        const permId = permissionIdByCode.get(code);
                                        const perm = permId ? permissionById.get(permId) : undefined;
                                        if (perm) {
                                          return renderPermissionRow(perm, item.path, {
                                            showSharedHint: true,
                                          });
                                        }
                                        return (
                                          <div
                                            key={code}
                                            className="rounded-md border border-dashed border-gray-200 bg-gray-50 p-3 text-xs text-gray-600"
                                          >
                                            <div className="font-mono font-medium text-gray-800">{code}</div>
                                            <div className="mt-1">
                                              No matching permission in the catalog. Add or seed this
                                              permission in admin so it can be assigned.
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {permissionsData && additionalPermissionCategories.length > 0 && (
                <div className="mt-8 border-t border-gray-200 pt-6">
                  <h4 className="text-base font-semibold text-gray-900">Additional API / legacy</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    These permissions are not tied to a sidebar page in the access map. Use them when
                    a feature needs backend-only capability.
                  </p>
                  <div className="mt-4 space-y-6">
                    {additionalPermissionCategories.map((category) => {
                      const categoryPermissionIds = category.permissions.map((p) => p.id);
                      const selectedInCategory = categoryPermissionIds.filter((permId) =>
                        selectedPermissions.has(permId)
                      ).length;
                      const allSelectedInCategory =
                        selectedInCategory === categoryPermissionIds.length;
                      const someSelectedInCategory =
                        selectedInCategory > 0 && selectedInCategory < categoryPermissionIds.length;

                      return (
                        <div key={category.code} className="rounded-lg border border-gray-200 p-4">
                          <div className="mb-3 flex items-center justify-between">
                            <div className="flex items-center">
                              <button
                                type="button"
                                onClick={() => handleSelectAllInCategory(category)}
                                className={`mr-3 flex h-4 w-4 items-center justify-center rounded border-2 ${
                                  allSelectedInCategory
                                    ? 'border-blue-600 bg-blue-600'
                                    : someSelectedInCategory
                                      ? 'border-blue-600 bg-blue-600'
                                      : 'border-gray-300 hover:border-gray-400'
                                }`}
                              >
                                {allSelectedInCategory && (
                                  <CheckSquare className="h-3 w-3 text-white" />
                                )}
                                {someSelectedInCategory && !allSelectedInCategory && (
                                  <div className="h-2 w-2 rounded-sm bg-white" />
                                )}
                              </button>
                              <h4 className="text-lg font-medium text-gray-900">{category.name}</h4>
                            </div>
                            <span className="text-sm text-gray-500">
                              {selectedInCategory}/{categoryPermissionIds.length} selected
                            </span>
                          </div>
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                            {category.permissions.map((permission) => {
                              const isSelected = selectedPermissions.has(permission.id);
                              return (
                                <label
                                  key={permission.id}
                                  className={`flex cursor-pointer items-start rounded-md border p-3 transition-colors ${
                                    isSelected
                                      ? 'border-blue-200 bg-blue-50'
                                      : 'border-gray-200 bg-white hover:bg-gray-50'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handlePermissionToggle(permission.id)}
                                    className="mt-0.5 mr-3 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium text-gray-900">
                                      {permission.name}
                                    </div>
                                    <div className="text-xs text-gray-500 font-mono">
                                      {permission.code}
                                    </div>
                                    <div className="mt-1 text-[11px] font-medium text-amber-700">
                                      API-only / legacy
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {permissionsData && permissionsData.categories.length === 0 && (
                <div className="py-8 text-center text-gray-500">No permissions available</div>
              )}
            </ContentCard>

            {errors.root && (
              <div className="alert alert-error">
                <div className="flex items-center">
                  <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {errors.root.message}
                </div>
              </div>
            )}

            <ContentCard>
              <div className="flex items-center justify-between">
                <Link to="/roles">
                  <Button variant="ghost" icon={<X className="h-4 w-4" />}>
                    Cancel
                  </Button>
                </Link>

                <Button
                  type="submit"
                  loading={isSubmitting}
                  icon={<Save className="h-4 w-4" />}
                >
                  {isEditing ? 'Update Role' : 'Create Role'}
                </Button>
              </div>
            </ContentCard>
          </form>
        )}
      </PageLayout>
    </AppShell>
  );
};
